<template>
    <app-layout v-if="isPageReady && isReferralMade" printClass="print-referral-summary">
        <common-content-spacer height="small">
            <common-page-header :heading="$t('pages.referrals.new.steps.confirmation.request_sent')"></common-page-header>

            <common-page-plain :hasLargePadding="true">
                <referrals-confirmation :heading="$t('pages.referrals.new.steps.confirmation.heading')" type="success">
                    <template #content>
                        <common-summary>
                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                                <common-user-name :user="referral.patient"></common-user-name>
                            </common-summary-item>

                            <common-summary-item v-if="selectedSlot">
                                <common-date :date="selectedSlot.date" :copy="selectedSlot.date.format('dddd')">
                                    <template #time>
                                        <common-slots :slots="[selectedSlot.slot]"></common-slots>
                                    </template>
                                </common-date>
                            </common-summary-item>

                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                                {{ referral.diagnosis }}
                            </common-summary-item>

                            <common-summary-item
                                :label="$t('pages.referrals.new.steps.confirmation.summary.to')"
                                :isInline="true"
                            >
                                <common-user-name :user="physician"></common-user-name>

                                <br />

                                <common-location-address v-if="location" :location="location"></common-location-address>

                                <p v-if="physician.phone">
                                    {{ $t('pages.referrals.new.steps.confirmation.summary.tel', { phone: physician.phone }) }}
                                </p>
                            </common-summary-item>
                        </common-summary>
                    </template>

                    <template #footer>
                        <common-buttons>
                            <common-button
                                :label="$t('pages.referrals.new.steps.confirmation.summary.print')"
                                :isSubmittable="false"
                                type="primary"
                                @click="printSummary()"
                            ></common-button>
                        </common-buttons>
                    </template>
                </referrals-confirmation>
            </common-page-plain>
        </common-content-spacer>
    </app-layout>

    <app-layout v-else-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageHeading">
                    <template #right>
                        <referrals-new-back
                            :route="{
                                name: 'referrals.new.results',
                                query: $route.params.originalQuery || $route.query,
                                params: { existingReferral: $route.params.existingReferral },
                            }"
                            :results="[physician]"
                        ></referrals-new-back>
                    </template>
                </common-page-header>
            </common-content-spacer>

            <common-page-split :showAside="!isOutsider">
                <template #aside>
                    <common-content-spacer height="medium">
                        <practice-member-card
                            v-if="$route.query.practiceType === PracticeTypeEnum.PHYSICIAN"
                            :physician="physician"
                            :isStandalone="false"
                            :showNetworkInclusionStatus="false"
                        ></practice-member-card>
                        <practice-card
                            v-if="$route.query.practiceType === PracticeTypeEnum.ANCILLARY"
                            :practice="ancillaryPractice"
                            :isStandalone="false"
                        ></practice-card>
                        <common-content-group>
                            <common-content-spacer height="medium">
                                <common-icon-info v-if="location" icon="location" size="huge">
                                    <common-location-address
                                        :location="location"
                                        :isNameDisplayed="true"
                                    ></common-location-address>
                                </common-icon-info>

                                <common-icon-info :href="`mailto:${physician.email}`" icon="email" size="huge">
                                    {{ physician.email || ancillaryPractice.email }}
                                </common-icon-info>
                            </common-content-spacer>
                        </common-content-group>
                    </common-content-spacer>
                </template>
                <template #main>
                    <template>
                        <common-content-wrapper width="large">
                            <common-content-spacer height="large">
                                <referrals-new-make-form
                                    :selectedSlot="selectedSlot?.slot"
                                    :practice="practice"
                                    :location="location"
                                    @startOver="selectSlot(null)"
                                    @sent="markReferralAsMade($event)"
                                ></referrals-new-make-form>
                            </common-content-spacer>
                        </common-content-wrapper>
                    </template>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import { importComponents } from '~/helpers'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import ReferralsNewMakeForm from 'components/forms/ReferralsNewMakeForm'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import UserTypeEnum from '@/enums/UserTypeEnum'
    import dayjs from 'dayjs'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralsNewPageMixin],

        components: {
            AppLayout,
            ReferralsNewMakeForm,
            PracticeMemberCard,
            PracticeCard,
            ...importComponents(require.context('components/form-elements')),
        },

        provide() {
            return {
                isReferralPool: false,
                sharedState: null,
            }
        },

        data() {
            return {
                physician: {},
                ancillaryPractice: {},
                selectedSlot: null,
                isReferralMade: false,
                PracticeTypeEnum: PracticeTypeEnum,
                isOutsider: false,
                outsider: {},
            }
        },

        loadData(self) {
            if (self.$route.query.practiceType === PracticeTypeEnum.ANCILLARY && !self.$route.query.outsider) {
                return {
                    ancillaryPractice: PracticesAPI.getPracticeById(self.$route.query.practiceId),
                }
            } else {
                if (!self.$route.query.outsider) {
                    return {
                        physician: PhysiciansAPI.getPhysicianData({
                            userId: self.$auth.getActiveMember()?.id ? self.$auth.getActiveMember()?.id : self.$auth.getUser().id,
                            physicianId: self.$route.query.physicianId,
                        }),
                    }
                } else {
                    return {
                        physician: {},
                    }
                }
            }
        },

        computed: {
            pageHeading() {
                if (this.$route.query.outsider) {
                    return this.$t('pages.referrals.new.title')
                }
                const practiceType = this.$route.query.practiceType
                return this.$t(`pages.referrals.new.steps.schedule.heading.${practiceType}`, {
                    name:
                        this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                            ? this.ancillaryPractice.name
                            : `${composeDisplayName(this.physician)} ${this.physician?.designation || ''}`,
                })
            },

            isSlotSelected() {
                return this.selectedSlot
            },

            practice() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.ancillaryPractice
                } else {
                    return this.$get(this.physician, UserTypeEnum.PRACTICE)
                }
            },

            location() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY && !this.$route.query.outsider) {
                    const mainLocation = {
                        address: this.ancillaryPractice.address,
                        city: this.ancillaryPractice.city,
                        state: this.ancillaryPractice.state,
                        zip: this.ancillaryPractice.zip,
                        isAvailabilitySet: this.ancillaryPractice.isAvailabilitySet,
                        id: 0,
                    }
                    const locations = [mainLocation, ...this.ancillaryPractice.locations]
                    return locations.find((item) => item.id === parseInt(this.$route.query.practiceLocationId))
                } else {
                    if (this.$route.query.practiceLocationId === '0') {
                        return this.$auth.getPractice()
                    } else {
                        return this.$auth
                            .getPractice()
                            .locations.find(({ id }) => id === parseInt(this.$route.query.practiceLocationId))
                    }
                }
            },
        },

        methods: {
            selectSlot(slot) {
                this.selectedSlot = slot
            },

            markReferralAsMade(referral) {
                if (referral.physician) {
                    this.physician = referral.physician
                }

                this.referral = referral
                this.isReferralMade = true
            },

            printSummary() {
                window.print()
            },

            resetPageData() {
                this.isReferralMade = false
                this.selectedSlot = null
            },
        },

        watch: {
            '$route.query'() {
                this.resetPageData()
            },
        },

        mounted() {
            if (this.$route.query.outsider) {
                this.isOutsider = true
                this.outsider = {
                    firstName: this.$route.query.firstName,
                    lastName: this.$route.query.lastName,
                    email: this.$route.query.email,
                }
            }
            if (this.$route.query.selfReferred) {
                this.selectedSlot = {
                    date: dayjs(this.$route.query.date),
                    slot: {
                        startTime: this.$route.query.startTime,
                        endTime: this.$route.query.endTime,
                        date: this.$route.query.date,
                        from: dayjs(this.$route.query.startTime, 'HH:mm').diff(dayjs().startOf('day'), 'minutes'),
                        to: dayjs(this.$route.query.endTime, 'HH:mm').diff(dayjs().startOf('day'), 'minutes'),
                    },
                }
            }
        },
    }
</script>
